import styled from "@emotion/styled";
import React from "react";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row } from "@src/components/Flex";
import { useConvert, useYield } from "@src/hooks/useLang";
import getTextWidth from "@src/utils/getTextWidth";
import { trunc } from "@src/utils/BN";

const Root = styled.div<{ width?: number }>`
  position: absolute;
  inset: 18px max(52px, env(safe-area-inset-right, 52px)) auto auto;
  background: #fff;
  padding: 6px 12px;
  width: ${({ width }) => width ?? 184}px;
  border-radius: 6px;
  z-index: 2;
`;

const font =
  "font-family: Nunito Sans; font-weight: bold;font-size: 12px; line-height: 18px;white-space:nowrap";

const MapOverallYieldLegend: React.FC = () => {
  const vm = useFieldsScreenVM();
  const { convertYield } = useConvert();
  const locYield = useYield();

  const legend = vm.overallYieldLegend;

  if (vm.overallMapView !== "avgYield" || legend.length === 0 || vm.selectedFieldId != null) {
    return null;
  }

  const totalCount = legend.reduce((sum, item) => sum + item.count, 0);
  const maxPercent = Math.max(
    ...legend.map((v) => (totalCount > 0 ? Math.round((v.count / totalCount) * 100) : 0))
  );

  const formatRange = (start: number, end: number): string =>
    `${trunc(convertYield(start))} - ${trunc(convertYield(end))}`;

  const maxWidth = Math.max(
    ...legend.map(({ start, end }) => getTextWidth(formatRange(start, end), font))
  );

  return (
    <Root width={maxWidth * 2 + 56}>
      <Text type="heading" color="#899093" size={12}>
        {locYield()}
      </Text>
      <SizedBox height={4} />
      {legend.map((v, i) => {
        const percent = totalCount > 0 ? Math.round((v.count / totalCount) * 100) : 0;
        return (
          <Row style={{ height: 16, marginBottom: 6 }} key={i}>
            <Text
              color="#494C5B"
              size={11}
              type="heading"
              style={{ marginRight: 8 }}
              nowrap
            >
              {formatRange(v.start, v.end)}
            </Text>
            <Row
              style={{
                height: "100%",
                padding: "0 4px",
                position: "relative",
              }}
              alignItems="center"
            >
              <div
                style={{
                  position: "absolute",
                  background: v.color,
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${maxPercent > 0 ? (percent / maxPercent) * 100 : 0}%`,
                }}
              />
              <Text
                color="#494C5B"
                size={11}
                type="heading"
                style={{
                  marginRight: 8,
                  zIndex: 1,
                  textShadow:
                    "1px 0 0 #fff, " +
                    "-1px 0 0 #fff, " +
                    "0 1px 0 #fff, " +
                    "0 -1px 0 #fff, " +
                    "1px 1px #fff, " +
                    "-1px -1px 0 #fff, " +
                    "1px -1px 0 #fff, " +
                    "-1px 1px 0 #fff",
                }}
              >
                {percent} %
              </Text>
            </Row>
          </Row>
        );
      })}
    </Root>
  );
};

export default observer(MapOverallYieldLegend);
